// Synchronisation avec le core THOR — port navigateur de scheduler._auto_sync.
// En un aller-retour : enrôlement paresseux, PUSH des events en file, PULL du
// delta de pannes depuis le curseur. No-op silencieux hors-ligne ou sans core.

import { PRODUIT, SYNC_INTERVAL_MS, TIER } from "../config";
import type { Fault } from "../domain/types";
import { CoreClient } from "../core/client";
import { empreinteDepuisClePublique } from "./identity";
import { getLicenseToken } from "./license";
import {
  getSyncState,
  pendingEvents,
  putBoard,
  putSyncState,
  updateEvent,
  upsertFault,
} from "../storage/db";

export interface SyncReport {
  ok: boolean;
  reason?: string;
  pushed: number;
  pulled: number;
}

async function ensureEnrolled(client: CoreClient): Promise<boolean> {
  const license = await getLicenseToken();
  // Déjà enrôlé ET aucune licence à faire valoir → rien à faire.
  if (client.identity.clientId && !license) return true;
  // Sinon on (ré)enrôle : si une licence est présente, le core PROMEUT le poste
  // (demo → VALIDATED_RSA) de façon idempotente → il recevra ensuite la base complète.
  // Fingerprint = identifiant stable par poste dérivé de la clé publique.
  // Même fonction que celle affichée dans l'onglet Licence : les deux doivent
  // rester identiques, sinon on lie la licence à une autre machine.
  const fingerprint = empreinteDepuisClePublique(client.identity.publicKeyHex);
  const cid = await client.enroll(PRODUIT, TIER, fingerprint, license);
  return cid !== null;
}

export async function syncNow(): Promise<SyncReport> {
  const client = await CoreClient.create();
  if (!client) return { ok: false, reason: "core désactivé", pushed: 0, pulled: 0 };
  if (!(await ensureEnrolled(client)))
    return { ok: false, reason: "enrôlement impossible (hors-ligne ?)", pushed: 0, pulled: 0 };

  const state = await getSyncState();
  const pending = await pendingEvents();
  const events = pending.map((e) => ({
    client_event_id: e.client_event_id,
    domaine: e.domaine,
    device_brand: e.device_brand,
    device_model: e.device_model,
    symptom: e.symptom,
    chosen_cause: e.chosen_cause,
    resolved: e.resolved,
    occurred_at: e.occurred_at,
  }));

  const resp = await client.sync(events, state.faults_cursor, state.boards_cursor);
  if (!resp) return { ok: false, reason: "core injoignable", pushed: 0, pulled: 0 };

  // PUSH confirmé : le core ingère par idempotence (client_event_id).
  for (const e of pending) {
    e.synced = true;
    await updateEvent(e);
  }

  // PULL : upsert du delta de pannes (PK = uuid du core).
  for (const f of resp.faults) {
    const fault: Fault = {
      id: f.id,
      domaine: f.domaine,
      device_brand: f.device_brand ?? null,
      device_model: f.device_model ?? null,
      symptom: f.symptom,
      probable_causes: f.probable_causes ?? [],
      source: f.source ?? "community",
      updated_at: f.updated_at,
    };
    await upsertFault(fault);
  }

  // PULL : upsert du delta de la KB board (PK = n° de carte). Le payload est
  // le BoardModel complet, stocké tel quel dans le store IndexedDB `boards`.
  const boards = resp.boards ?? [];
  for (const b of boards) {
    if (b?.payload?.id) await putBoard(b.payload);
  }

  // Curseur = high-water mark renvoyé par le core (faults + boards alignés).
  await putSyncState({
    id: "singleton",
    faults_cursor: resp.server_time,
    boards_cursor: resp.server_time,
    last_sync_at: new Date().toISOString(),
  });

  return { ok: true, pushed: events.length, pulled: resp.faults.length + boards.length };
}

let _timer: number | null = null;

/** Lance une sync immédiate (en tâche de fond) puis périodique tant que l'app est ouverte. */
export function startAutoSync(onReport?: (r: SyncReport) => void): void {
  const run = () => {
    void syncNow().then((r) => onReport?.(r));
  };
  run();
  if (_timer === null) _timer = window.setInterval(run, SYNC_INTERVAL_MS);
  // Re-sync quand on repasse en ligne.
  window.addEventListener("online", run);
}
